import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2Icon,
  CircleIcon,
  Loader2Icon,
  PauseIcon,
  PlayIcon,
  ShieldAlertIcon,
} from 'lucide-react';
import {
  account,
  backup as backupApi,
  BridgeError,
  bridge,
  type BackupSnapshot,
  type DataTypeCatalog,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { InfoCard, TaskProgressCard, taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, FOOTNOTE } from '@/components/ui';
import { RowList } from '@/components/RowList';
import { SectionHeader } from '@/components/SectionHeader';

/**
 * 薯薯全能通 · 笔记备份。
 *
 * 版式照搬搬小音 `pages/bxy/BackupPage.tsx`：账号卡 → 数据类型勾选 → 进度 → 开始 / 暂停 / 继续。
 * 现阶段只备份笔记（点赞 / 收藏 / 关注不做）。**类型不写死**：由原生 `backup.types` 按平台下发
 * （小红书叫「笔记」、抖音叫「作品」），以后加类型页面不用改；
 * `supported=false`（原生还没实现该平台的备份）时只展示、按钮置灰。
 */

const STATUS_LABELS: Record<string, string> = {
  running: '备份中',
};

const TYPE_STATUS_TEXT: Record<string, string> = {
  idle: '等待中',
  running: '备份中',
  paused: '已暂停',
  completed: '已完成',
  failed: '失败',
};

/** 备份中用独立图标区分「已选」和「跑完了」。 */
function StatusIcon({ status }: { status: string }) {
  const box = 'flex h-4 w-4 shrink-0 items-center justify-center';
  const icon =
    status === 'completed' ? (
      <CheckCircle2Icon className="text-primary size-4" />
    ) : status === 'running' ? (
      <Loader2Icon className="text-primary size-4 animate-spin" />
    ) : status === 'paused' ? (
      <PauseIcon className="text-muted-foreground size-4" />
    ) : status === 'failed' ? (
      <ShieldAlertIcon className="text-destructive size-4" />
    ) : (
      <CircleIcon className="text-muted-foreground/40 size-4" />
    );
  return <span className={box}>{icon}</span>;
}

export function ShushuBackupPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
  } = useAccounts();
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<DataTypeCatalog | null>(null);
  /** App 版本太旧、没有 backup.types */
  const [outdated, setOutdated] = useState(false);
  const [types, setTypes] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<BackupSnapshot | null>(null);
  const [busy, setBusy] = useState(false);

  const currentAccount = accounts.find((a) => a.id === selectedId) ?? null;
  // 首屏要等类型目录到位（或已出错）再撤骨架
  const loaded =
    !infoLoading && !accountsLoading && (!currentAccount || catalog !== null || outdated || error !== null);
  useAppReady(loaded);
  useGroupedSurface();

  // 进度用事件推送，不轮询（PROTOCOL.md §5）
  useEffect(() => bridge.on('backup.progress', (payload) => setSnapshot(payload as BackupSnapshot)), []);

  // 类型目录：随账号（平台）变；默认全选
  const loadCatalog = useCallback(() => {
    if (!currentAccount) return;
    backupApi
      .types(currentAccount.id)
      .then((next) => {
        setCatalog(next);
        setTypes((prev) => (prev.length ? prev.filter((k) => next.types.some((t) => t.key === k)) : next.types.map((t) => t.key)));
      })
      .catch((e) => {
        if (e instanceof BridgeError && e.code === 'unknown_method') setOutdated(true);
        else setError(describeError(e));
      });
  }, [currentAccount?.id]);

  useEffect(() => {
    setCatalog(null);
    setTypes([]);
    setSnapshot(null);
    loadCatalog();
  }, [loadCatalog]);

  const supported = catalog?.supported ?? false;

  // 支持时才有任务状态可拉
  useEffect(() => {
    if (!currentAccount || !supported) return;
    let cancelled = false;
    backupApi
      .status(currentAccount.id)
      .then((next) => {
        if (!cancelled) setSnapshot((prev) => prev ?? next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id, supported]);

  const state = snapshot;
  const active = state?.state === 'running' || state?.state === 'paused';
  const writableCount = accounts.filter((a) => a.canWrite).length;
  const canWrite = currentAccount?.canWrite ?? false;

  // 一次备份跑完后刷新本机条数
  useEffect(() => {
    if (state?.state === 'completed') loadCatalog();
  }, [state?.state, loadCatalog]);

  const switchAccount = useCallback(async () => {
    try {
      setSelectedId((await account.pick('authenticated')).id);
    } catch {
      // 用户取消：保留当前账号
    }
  }, [setSelectedId]);

  const run = useCallback(
    async (action: 'start' | 'pause' | 'resume') => {
      if (!currentAccount || busy) return;
      setBusy(true);
      setError(null);
      try {
        const next =
          action === 'start'
            ? await backupApi.start(currentAccount.id, types)
            : action === 'pause'
              ? await backupApi.pause(currentAccount.id)
              : await backupApi.resume(currentAccount.id);
        setSnapshot(next);
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, currentAccount, types],
  );

  const toggleType = useCallback(
    (key: string) => {
      if (busy || active) return;
      setTypes((prev) => {
        const next = prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key];
        if (!next.length) {
          setError('至少保留一种备份数据');
          return prev;
        }
        setError(null);
        return next;
      });
    },
    [active, busy],
  );

  const typeDefs = catalog?.types ?? [];
  const typeStateOf = (key: string) => state?.types?.find((t) => t.type === key);
  const labelOf = (key: string) => typeDefs.find((t) => t.key === key)?.label ?? key;
  const done = types.filter((k) => {
    const st = typeStateOf(k)?.status;
    return st === 'completed' || st === 'failed';
  }).length;
  const migrationHref = `/shushu/migration${currentAccount ? `?accountId=${encodeURIComponent(currentAccount.id)}` : ''}`;

  return (
    <PageShell title="数据备份" description="把账号的笔记备份到本机">
      {currentAccount ? (
        <AccountCard
          account={currentAccount}
          label={[currentAccount.platformLabel, currentAccount.handle].filter(Boolean).join(' · ') || '备份账号'}
          showId={false}
          detail={writableCount > 1 ? `${writableCount} 个账号可切换` : undefined}
          onClick={writableCount > 1 && !active ? () => void switchAccount() : undefined}
        />
      ) : null}

      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && isNative && !currentAccount ? (
        <InfoCard title="没有可备份的账号" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          请先在 App 内登录一个平台账号。
        </InfoCard>
      ) : null}

      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持备份，请更新到最新版本后再试。
        </InfoCard>
      ) : null}

      {catalog && !supported ? (
        <InfoCard title="即将支持" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          {catalog.note ?? `${currentAccount?.platformLabel ?? '该平台'}的备份正在开发中，敬请期待。`}
        </InfoCard>
      ) : null}

      {currentAccount && catalog ? (
        <>
          {active && state ? (
            <TaskProgressCard
              title={taskStatusLabel(state.state, STATUS_LABELS) ?? state.state}
              right={`已完成 ${done} / ${types.length} 项`}
              ratio={state.progress}
              paused={state.state === 'paused'}
              detail={
                state.state === 'paused'
                  ? '已暂停，点「继续备份」接着跑'
                  : state.type
                    ? `正在备份：${labelOf(state.type)}`
                    : '准备中…'
              }
            />
          ) : null}

          {state?.state === 'completed' ? (
            <TaskProgressCard
              finished
              bar={false}
              title="备份完成"
              detail={state.processed != null ? `已处理 ${state.processed} 条` : undefined}
            />
          ) : null}

          {state?.state === 'failed' ? (
            <TaskProgressCard
              bar={false}
              icon={<ShieldAlertIcon className="text-destructive size-5 shrink-0" />}
              title={taskStatusLabel(state.state) ?? '备份失败'}
              detail={state.error ? describeError(state.error) : '可重新开始备份'}
            />
          ) : null}

          <Card className={CARD}>
            <SectionHeader title="备份内容" />
            <RowList>
              {typeDefs.map((def) => {
                const row = typeStateOf(def.key);
                const status = row?.status ?? 'idle';
                const selected = types.includes(def.key);
                // 运行中的类型显示实时条数，其余显示本机已备份总数
                const live = active && selected && status === 'running';
                const count = live ? (row?.processed ?? 0) : (def.count ?? 0);
                return (
                  <SelectRow
                    key={def.key}
                    checked={selected}
                    onCheckedChange={() => toggleType(def.key)}
                    disabled={active || !canWrite || !supported}
                    icon={active ? <StatusIcon status={status} /> : undefined}
                    detail={`${count} 条`}
                    footer={active && selected ? (TYPE_STATUS_TEXT[status] ?? status) : undefined}
                  >
                    {def.label}
                  </SelectRow>
                );
              })}
            </RowList>
          </Card>
          <p className={FOOTNOTE}>
            {!canWrite
              ? '该账号登录已失效，请先在 App 内重新登录后再备份。'
              : active
                ? '备份进行中，完成后可看到本次结果。'
                : '条数为本机已备份数量。再次备份只拉新增内容，已备份的不会重复下载。'}
          </p>

          <div className="flex flex-col gap-3">
            {!active ? (
              <>
                <Button
                  className="w-full"
                  loading={busy}
                  disabled={!supported || types.length === 0 || !canWrite}
                  onClick={() => void run('start')}
                >
                  {busy ? '处理中…' : !supported ? '即将支持' : state?.state === 'completed' ? '再次备份' : '开始备份'}
                </Button>
                <Button variant="secondary" className="w-full" onClick={() => (window.location.href = migrationHref)}>
                  迁移数据
                </Button>
              </>
            ) : null}
            {state?.state === 'running' ? (
              <Button variant="secondary" className="w-full" loading={busy} onClick={() => void run('pause')}>
                <PauseIcon data-icon="inline-start" />
                暂停
              </Button>
            ) : null}
            {state?.state === 'paused' || state?.state === 'failed' ? (
              <Button className="w-full" loading={busy} onClick={() => void run('resume')}>
                <PlayIcon data-icon="inline-start" />
                继续备份
              </Button>
            ) : null}
          </div>
        </>
      ) : null}

      {(error ?? accountError) ? <p className="text-destructive px-1 text-sm">{error ?? accountError}</p> : null}

      {!loaded ? <PageSkeleton kind="backup" rows={5} /> : null}
    </PageShell>
  );
}
